/* /app/paid-library — logged-in view for bundle owners. Shows all 15 books
   with download buttons that use signed, 5-minute URLs. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, Download, Loader2, Lock, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AppPaidLibrary() {
  const [status, setStatus] = useState(null);   // {owns_bundle, purchased_at, book_count}
  const [manifest, setManifest] = useState(null);   // {books, expires_in_sec}
  const [refreshing, setRefreshing] = useState(false);

  const loadManifest = async () => {
    setRefreshing(true);
    try {
      const s = await api.get("/library-bundle/me");
      setStatus(s.data);
      if (s.data.owns_bundle) {
        const m = await api.get("/library-bundle/manifest");
        setManifest(m.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load library");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadManifest(); }, []);

  if (!status) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" /> Loading your library…
      </div>
    );
  }

  if (!status.owns_bundle) {
    return (
      <div className="p-8 max-w-2xl" data-testid="paid-library-locked">
        <div className="card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-[var(--primary)]/20 rounded-full flex items-center justify-center">
              <Lock size={24} className="text-[var(--primary-dark)]" />
            </div>
            <div className="flex-1">
              <h2 className="font-display font-black text-2xl">Library not unlocked yet</h2>
              <p className="text-[var(--ink-2)] mt-2 text-sm">
                Get lifetime access to all 15 iSpeedToClose eBooks for a one-time $47.
              </p>
              <Link
                to="/library-bundle"
                className="btn-money mt-4 inline-flex items-center gap-2"
                data-testid="paid-library-unlock"
              >
                Unlock the Library → $47
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="paid-library-page">
      <div className="topbar">
        <div>
          <div className="crumb flex items-center gap-1.5">
            <BookOpen size={11} /> Your Library
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            The Wholesaling Library
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            All 15 eBooks. Lifetime access. Download anytime.
          </p>
        </div>
        <div className="ml-auto">
          <button onClick={loadManifest} disabled={refreshing}
                  className="btn-outline flex items-center gap-1.5"
                  data-testid="paid-library-refresh">
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            <span className="text-xs">Refresh links</span>
          </button>
        </div>
      </div>

      <div className="text-xs text-[var(--ink-3)] mb-4">
        Download links expire every 5 minutes for security. Just refresh if a link stops working.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="paid-library-grid">
        {manifest?.books?.map((b, i) => (
          <div key={b.slug} className="card flex flex-col justify-between"
               data-testid={`paid-library-card-${b.slug}`}>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--primary-dark)] font-bold mb-1">
                Vol {i + 1}
              </div>
              <div className="font-display font-black text-lg leading-tight">{b.title}</div>
              <div className="text-xs text-[var(--ink-3)] mt-1 leading-snug">{b.subtitle}</div>
            </div>
            <div className="mt-4">
              <a href={b.download_url} target="_blank" rel="noopener noreferrer"
                 className="btn-money w-full flex items-center justify-center gap-1.5 text-sm"
                 data-testid={`paid-library-download-${b.slug}`}>
                <Download size={13} /> Download PDF <ArrowRight size={12} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
